import SvgDot from '@sats-group/icons/18/dot';
import Chip from '@sats-group/ui-lib/react/chip';
import Text from '@sats-group/ui-lib/react/text';
import Toggle from '@sats-group/ui-lib/react/toggle';
import cn from 'classnames';
import React, { useState } from 'react';
import ContentContainer from '../content-container/content-container';
import type { SegmentedSection as Props } from './segmented-section.types';
import { alignments, appearances } from './segmented-section.types';

const getDefaultSegmentId = (view: Props['views'][number]) =>
  view.segments.some(segment => segment.id === view.defaultSegmentId)
    ? (view.defaultSegmentId ?? '')
    : (view.segments[0]?.id ?? '');

const SegmentedSection: React.FunctionComponent<Props> & {
  alignments: typeof alignments;
  appearances: typeof appearances;
} = ({
  alignment = alignments.left,
  appearance = appearances.light,
  className,
  description,
  eyebrow,
  title,
  toggle,
  views,
}) => {
  const [activeViewIndex, setActiveViewIndex] = useState<0 | 1>(0);
  const [selectedSegmentId, setSelectedSegmentId] = useState(() =>
    getDefaultSegmentId(views[0])
  );
  const activeView = views[activeViewIndex] ?? views[0];
  const selectedSegment =
    activeView.segments.find(segment => segment.id === selectedSegmentId) ??
    activeView.segments[0];
  const showSegmentChips = activeView.segments.length > 1;
  const isFixed = appearance === appearances.fixed;

  const slot = selectedSegment ? (
    <div
      className="segmented-section__slot"
      key={`${activeView.id}-${selectedSegment.id}`}
    >
      {selectedSegment.slot}
    </div>
  ) : null;

  return (
    <section
      className={cn(
        'segmented-section',
        `segmented-section--alignment-${alignment}`,
        `segmented-section--appearance-${appearance}`,
        className
      )}
    >
      <ContentContainer theme={ContentContainer.themes.wide}>
        <div className="segmented-section__header">
          <div className="segmented-section__heading">
            {eyebrow ? (
              <Text className="segmented-section__eyebrow">{eyebrow}</Text>
            ) : null}
            <Text
              className="segmented-section__title"
              elementName="h2"
              italic={isFixed}
              size={Text.sizes.headline1}
              theme={isFixed ? Text.themes.headline : Text.themes.emphasis}
            >
              {title}
            </Text>
          </div>
          {description ? (
            <Text className="segmented-section__description" elementName="p">
              {description}
            </Text>
          ) : null}
        </div>

        {showSegmentChips || toggle ? (
          <div
            className={cn('segmented-section__controls', {
              'segmented-section__controls--with-toggle': toggle,
            })}
          >
            {showSegmentChips ? (
              <ul className="segmented-section__segments">
                {activeView.segments.map(segment => {
                  const isSelected = selectedSegment?.id === segment.id;

                  return (
                    <li key={segment.id}>
                      <Chip
                        aria-pressed={isSelected}
                        customIcon={<SvgDot />}
                        elementName="button"
                        isSelected={isSelected}
                        onClick={() => setSelectedSegmentId(segment.id)}
                        text={segment.label}
                        type="button"
                        variant={
                          isFixed ? Chip.variants.fixed : Chip.variants.light
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {toggle ? (
              <div className="segmented-section__toggle">
                <Toggle
                  checked={activeViewIndex === 1}
                  label={toggle.label}
                  name={toggle.name}
                  onChange={event => {
                    const nextViewIndex = event.currentTarget.checked ? 1 : 0;
                    const nextView = views[nextViewIndex];
                    if (!nextView) {
                      return;
                    }

                    setActiveViewIndex(nextViewIndex);
                    setSelectedSegmentId(getDefaultSegmentId(nextView));
                  }}
                  showBackground
                  value={toggle.value}
                  variant={
                    isFixed ? Toggle.variants.fixed : Toggle.variants.default
                  }
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {slot}
      </ContentContainer>
    </section>
  );
};

SegmentedSection.alignments = alignments;
SegmentedSection.appearances = appearances;

export default SegmentedSection;
